/**
 * Aviso de tela: o `Banner` da Kumo com ícone por gravidade.
 *
 * Substitui o `Alert` do antd. Três gravidades, e só três: informação que qualifica o
 * número, ressalva que muda a leitura dele, e erro que invalida a comparação.
 */

import type { ReactNode } from "react";
import { Banner } from "@cloudflare/kumo";
import { InfoIcon, WarningCircleIcon, WarningIcon } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { PANEL_VARIANTS } from "./Panel.js";

/** Gravidade do aviso. */
export type NoticeTone = "info" | "warning" | "error";

/** Variante do `Banner` para cada gravidade. */
const BANNER_VARIANT: Readonly<Record<NoticeTone, "default" | "alert" | "error">> = {
  info: "default",
  warning: "alert",
  error: "error",
};

/** Entradas do aviso. */
interface NoticeProps {
  readonly tone: NoticeTone;
  readonly title: string;
  readonly description?: ReactNode;
}

/** Ícone de cada gravidade, para o aviso ser reconhecido antes de ser lido. */
function noticeIcon(tone: NoticeTone): React.ReactElement {
  if (tone === "error") {
    return <WarningCircleIcon weight="fill" />;
  }
  if (tone === "warning") {
    return <WarningIcon weight="fill" />;
  }
  return <InfoIcon weight="fill" />;
}

/** Aviso de largura total, com a mesma entrada animada dos painéis. */
export function Notice({ tone, title, description }: NoticeProps): React.ReactElement {
  return (
    <motion.div variants={PANEL_VARIANTS}>
      <Banner
        variant={BANNER_VARIANT[tone]}
        icon={noticeIcon(tone)}
        title={title}
        {...(description === undefined ? {} : { description })}
      />
    </motion.div>
  );
}
